import { Spinner, Typography, IconButton } from '@material-tailwind/react'
import { AiOutlinePlus } from 'react-icons/ai'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ExperiencePreviewCard from '../ViewDashboard/ExperiencePreviewCard'
import { AccordionCaret } from '../components/AccordionCaret'
import { usePageTitle } from '../hooks/toolkit'
import { useUserExperiences } from '../hooks/useUserExperiences'
import { useAccessibleExperiences } from '../hooks/useAccessibleExperiences'
import { useStoredState } from '../hooks/useStoredState'
import NoUploads from '../components/NoUploads'
import { useFeatureAuth, useFeatureFlag } from '../hooks/authorization'
import { useSessionContext } from '../contexts/SessionContext'
import { FEATURES, FEATURE_FLAGS } from '../utils/constants/features.constants'
import { AGAMI_GLOBAL_EXPERIENCE_ID } from '../utils/constants/db.constants'
import { SHARED_EXPERIENCES_SECTION_KEY } from '../utils/constants/localStorage.constants'
import { roleLabel } from '../utils/helpers/sharing.helpers'
import Unauthorized from './Unauthorized'

const DashboardExperiences = () => {
  const { t } = useTranslation()
  usePageTitle('Experience Library | Agami Dashboard')
  const { user } = useSessionContext()
  const hasOnde = useFeatureFlag(FEATURE_FLAGS.ONDE)

  // Two data paths, neither knows about the other; the flag only picks which one
  // runs. `enabled` keeps the inactive path from firing a useless request.
  const {
    experienceList,
    isLoading: isLoadingOwned,
    refetch: refetchOwned,
  } = useUserExperiences({
    excludeDefaultExperience: true,
    enabled: !hasOnde,
  })
  const {
    experiences: accessibleExperiences,
    isLoading: isLoadingAccessible,
    refetch: refetchAccessible,
  } = useAccessibleExperiences({ enabled: hasOnde })

  const isLoading = hasOnde ? isLoadingAccessible : isLoadingOwned
  const refetch = hasOnde ? refetchAccessible : refetchOwned

  // The new endpoint returns owned AND shared in one call: partition client-side.
  // `!exp.is_default` mirrors excludeDefaultExperience on the historic path.
  const myExperiences = hasOnde
    ? (accessibleExperiences?.filter((exp) => exp.owner_id === user?.id && !exp.is_default) ?? null)
    : experienceList
  // Agami Global is excluded by id (decided 2026-08-20): handle_new_user subscribes
  // EVERY user to it, so without this it would sit in "shared with me" for the whole
  // platform — the section is for experiences someone chose to share.
  const sharedExperiences =
    accessibleExperiences?.filter(
      (exp) => exp.owner_id !== user?.id && exp.id !== AGAMI_GLOBAL_EXPERIENCE_ID
    ) ?? []

  // From here on the page is driven by DATA, not by the flag: section headers exist
  // only when there is something shared, so for everyone else the page is unchanged.
  const hasSharedExperiences = sharedExperiences.length > 0

  const [isSharedSectionOpen, setIsSharedSectionOpen] = useStoredState<boolean>({
    key: SHARED_EXPERIENCES_SECTION_KEY,
    defaultValue: true,
  })

  const expAuth = useFeatureAuth(FEATURES.EXP_ACCESS_CODES)

  if (!expAuth) {
    return <Unauthorized />
  }

  return (
    <>
      <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-start gap-4 py-4">
        <div className="flex flex-col lg:max-w-[60%]">
          <Typography variant="h1" className="text-2xl md:text-5xl" data-testid="experiences-library-title">
            {t('dashboard.experiencesLibraryTitle')}
          </Typography>
          <Typography className="dark:text-gray-200">
            {t('dashboard.experiencesLibraryDescription')}
          </Typography>
        </div>
        <Link to="/upload/experience">
          <div className="flex items-center space-x-4">
            <IconButton size="sm" className="rounded-full">
              <AiOutlinePlus className="text-[20px]" />
            </IconButton>
            <Typography className="font-bold dark:text-gray-200">
              {t('dashboard.createExperience')}
            </Typography>
          </div>
        </Link>
      </div>

      {hasSharedExperiences && (
        <Typography variant="h2" className="text-xl md:text-2xl pb-4" data-testid="my-experiences-title">
          {t('dashboard.myExperiences')}
        </Typography>
      )}

      <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading && <Spinner className="h-6 w-6" />}

        {!isLoading && (!myExperiences || myExperiences.length === 0) && (
          <div className="w-full col-span-3 text-center">
            <NoUploads>
              <Typography className="dark:text-gray-200">{t('dashboard.noExperiencesYet')}</Typography>
            </NoUploads>
          </div>
        )}

        {!isLoading &&
          myExperiences?.map((exp) => (
            <div key={exp.id} className="flex-1 mb-4 basis-1/4">
              <ExperiencePreviewCard experience={exp} refetchExpList={refetch} />
            </div>
          ))}
      </div>

      {hasSharedExperiences && (
        <section className="w-full pt-8">
          <button
            type="button"
            className="w-full flex items-center gap-2 pb-4 text-left"
            onClick={() => setIsSharedSectionOpen((isOpen) => !isOpen)}
            data-testid="shared-experiences-toggle"
          >
            <Typography variant="h2" className="text-xl md:text-2xl" data-testid="shared-experiences-title">
              {t('dashboard.sharedWithMe', { count: sharedExperiences.length })}
            </Typography>
            <AccordionCaret rotate={isSharedSectionOpen} />
          </button>

          {isSharedSectionOpen && (
            <div
              className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
              data-testid="shared-experiences-grid"
            >
              {sharedExperiences.map((exp) => (
                <div key={exp.id} className="flex-1 mb-4 basis-1/4">
                  <ExperiencePreviewCard
                    experience={exp}
                    refetchExpList={refetch}
                    readOnly
                    roleLabel={roleLabel(exp.role) ?? undefined}
                    to={`/shared-experiences/${exp.id}`}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  )
}

export default DashboardExperiences
